// =============================================
// Deduplication Service — normalizacja pytań + hashing + semantic dedup
// =============================================

import type { NodePreview, PromptModel } from '@/types/thematicMaps';
import { generateDeduplication } from '@/services/thematicMapService';
import { logDebug, logPublic } from '@/services/contentEngine/contentLogger';
import { loadPrompt, fillTemplate } from '@/services/llm/promptLoader';
import { openaiProvider } from '@/services/llm/openaiProvider';

/**
 * Normalizuje canonical question do jednolitej formy.
 * Usuwa znaki specjalne, kompresuje spacje, stosuje zamiany semantyczne.
 */
export function normalizeQuestion(q: string): string {
  let norm = q.toLowerCase().trim();

  // Usun znaki specjalne (zostawiajac litery PL, cyfry, spacje)
  norm = norm.replace(/[^a-ząćęłńóśźżü0-9\s]/g, '');

  // Kompresja spacji
  norm = norm.replace(/\s+/g, ' ').trim();

  // Ujednolicenia semantyczne (PL)
  const replacements: [RegExp, string][] = [
    [/\bco to jest\b/g, 'czym jest'],
    [/\bw jaki sposob\b/g, 'jak'],
    [/\bjaki jest\b/g, 'czym jest'],
    [/\bjakie sa\b/g, 'czym sa'],
    [/\bdlaczego warto\b/g, 'dlaczego'],
    [/\bczy warto\b/g, 'dlaczego'],
    [/\bnajlepsze sposoby na\b/g, 'jak'],
    [/\bnajlepsze metody\b/g, 'jak'],
  ];

  for (const [pattern, replacement] of replacements) {
    norm = norm.replace(pattern, replacement);
  }

  return norm;
}

/**
 * Generuje hash z znormalizowanego pytania.
 * Używa prostego, deterministycznego hasha (djb2 + hex).
 */
export function hashQuestion(q: string): string {
  const normalized = normalizeQuestion(q);
  return simpleHash(normalized);
}

/**
 * Prosty, deterministyczny hash (djb2 variant).
 * Nie kryptograficzny, ale wystarczający dla anti-dupe w ramach jednej mapy.
 */
function simpleHash(str: string): string {
  let hash1 = 5381;
  let hash2 = 52711;

  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash1 = ((hash1 << 5) + hash1 + char) & 0xffffffff;
    hash2 = ((hash2 << 5) + hash2 + char) & 0xffffffff;
  }

  // Zwracamy 16-znakowy hex (64 bity)
  const h1 = (hash1 >>> 0).toString(16).padStart(8, '0');
  const h2 = (hash2 >>> 0).toString(16).padStart(8, '0');
  return h1 + h2;
}

/**
 * Sprawdza duplikaty w zbiorze istniejących node'ów.
 * Zwraca level: unique | warning | duplicate
 */
export function checkDuplicate(
  newQuestion: string,
  newEntityRaw: string,
  newRole: string,
  existingNodes: Array<{
    canonical_question_hash: string;
    primary_entity_raw: string;
    document_role: string;
    title: string;
    slug: string;
  }>
): {
  level: 'unique' | 'warning' | 'duplicate';
  reason?: string;
  matchSlug?: string;
} {
  const newHash = hashQuestion(newQuestion);

  // 1. Twardy duplikat: ten sam hash pytania
  for (const node of existingNodes) {
    if (node.canonical_question_hash === newHash) {
      return {
        level: 'duplicate',
        reason: `Identyczne pytanie jak "${node.title}"`,
        matchSlug: node.slug,
      };
    }
  }

  // 2. Ostrzezenie: ta sama encja + rola
  const newEntityNorm = newEntityRaw.toLowerCase().trim();
  const newRoleNorm = newRole.toLowerCase().trim();

  for (const node of existingNodes) {
    const existingEntityNorm = node.primary_entity_raw.toLowerCase().trim();
    const existingRoleNorm = node.document_role.toLowerCase().trim();

    if (existingEntityNorm === newEntityNorm && existingRoleNorm === newRoleNorm) {
      return {
        level: 'warning',
        reason: `Ta sama encja + rola jak "${node.title}"`,
        matchSlug: node.slug,
      };
    }
  }

  return { level: 'unique' };
}

// =============================================
// Semantic dedup — LLM-based intent comparison
// =============================================

export function tokenize(text: string): string[] {
  return text.toLowerCase().replace(/[^a-ząćęłńóśźż0-9\s]/g, '').split(/\s+/).filter(w => w.length > 2);
}

export function jaccardSimilarity(a: string[], b: string[]): number {
  const setA = new Set(a);
  const setB = new Set(b);
  const intersection = [...setA].filter(x => setB.has(x)).length;
  const union = new Set([...setA, ...setB]).size;
  return union === 0 ? 0 : intersection / union;
}

export interface SemanticDedupCandidate {
  indexA: number;
  indexB: number;
  reason: string;
}

/**
 * Find candidate pairs for semantic dedup (pre-filter before LLM).
 * Not O(n²) — filters by: same entity, same seed+role, similar title, similar question.
 */
export function findDedupCandidates(
  previews: Array<{
    title: string;
    primary_entity: string;
    document_role: string;
    canonical_question: string;
    duplicate_level: string;
  }>,
): SemanticDedupCandidate[] {
  const candidates: SemanticDedupCandidate[] = [];
  const uniqueIndices = previews.map((p, i) => ({ ...p, i })).filter(p => p.duplicate_level === 'unique');

  for (let a = 0; a < uniqueIndices.length; a++) {
    for (let b = a + 1; b < uniqueIndices.length; b++) {
      const nodeA = uniqueIndices[a];
      const nodeB = uniqueIndices[b];

      // Check: same primary_entity
      if (nodeA.primary_entity.toLowerCase() === nodeB.primary_entity.toLowerCase() && nodeA.primary_entity) {
        candidates.push({ indexA: nodeA.i, indexB: nodeB.i, reason: 'same_entity' });
        continue;
      }

      // Check: same document_role
      if (nodeA.document_role === nodeB.document_role) {
        // Also check title similarity
        const titleSim = jaccardSimilarity(tokenize(nodeA.title), tokenize(nodeB.title));
        if (titleSim > 0.5) {
          candidates.push({ indexA: nodeA.i, indexB: nodeB.i, reason: `same_role+similar_title(${(titleSim*100).toFixed(0)}%)` });
          continue;
        }
      }

      // Check: similar canonical_question
      const questionSim = jaccardSimilarity(
        tokenize(nodeA.canonical_question),
        tokenize(nodeB.canonical_question),
      );
      if (questionSim > 0.4) {
        candidates.push({ indexA: nodeA.i, indexB: nodeB.i, reason: `similar_question(${(questionSim*100).toFixed(0)}%)` });
      }
    }
  }

  return candidates;
}

/**
 * Run semantic dedup on node previews using the deduplication LLM prompt.
 * Mutates previews[].duplicate_level and duplicate_reason in place.
 */
export async function checkSemanticDuplicates(
  previews: NodePreview[],
  promptTemplate: string,
  model: PromptModel,
): Promise<{ checkedPairs: number; upgraded: number }> {
  const candidates = findDedupCandidates(previews);

  if (candidates.length === 0) {
    return { checkedPairs: 0, upgraded: 0 };
  }

  // Limit: max 15 pairs to avoid too many LLM calls
  const limitedCandidates = candidates.slice(0, 15);
  let upgraded = 0;

  // Process pairs in parallel batches of 3
  for (let i = 0; i < limitedCandidates.length; i += 3) {
    const batch = limitedCandidates.slice(i, i + 3);

    const results = await Promise.all(
      batch.map(async (pair) => {
        const nodeA = previews[pair.indexA];
        const nodeB = previews[pair.indexB];

        // Pre-filter: różna encja = nie duplikat, skip LLM
        const entityA = nodeA.primary_entity?.toLowerCase().trim();
        const entityB = nodeB.primary_entity?.toLowerCase().trim();
        if (entityA && entityB && entityA !== entityB) {
          logDebug("Dedup", `SKIP: "${nodeA.title}" vs "${nodeB.title}" (diff entity)`);
          return { pair, result: { success: true as const, data: { same_intent: "no" as const, confidence: 1.0, reason: "pre-filter: different entity" } } };
        }

        const vars = {
          node_a_title: nodeA.title,
          node_a_question: nodeA.canonical_question,
          node_a_entity: nodeA.primary_entity,
          node_a_role: nodeA.document_role,
          node_b_title: nodeB.title,
          node_b_question: nodeB.canonical_question,
          node_b_entity: nodeB.primary_entity,
          node_b_role: nodeB.document_role,
        };

        const result = await generateDeduplication(promptTemplate, vars, model);
        return { pair, result };
      }),
    );

    for (const { pair, result } of results) {
      if (!result.success || !result.data) continue;

      const { same_intent, confidence } = result.data;

      if (same_intent === 'yes' && confidence >= 0.85) {
        // High confidence duplicate
        previews[pair.indexB].duplicate_level = 'duplicate';
        previews[pair.indexB].duplicate_reason = `Semantyczny duplikat: "${previews[pair.indexA].title}" (${(confidence*100).toFixed(0)}% pewności)`;
        previews[pair.indexB].duplicate_match_slug = previews[pair.indexA].slug;
        upgraded++;
      } else if ((same_intent === 'yes' && confidence >= 0.6) || (same_intent === 'maybe' && confidence > 0.6)) {
        // Medium confidence — warning only
        if (previews[pair.indexB].duplicate_level === 'unique') {
          previews[pair.indexB].duplicate_level = 'warning';
          previews[pair.indexB].duplicate_reason = `Możliwy duplikat: "${previews[pair.indexA].title}" (${result.data.reason})`;
          previews[pair.indexB].duplicate_match_slug = previews[pair.indexA].slug;
          upgraded++;
        }
      }
    }
  }

  return { checkedPairs: limitedCandidates.length, upgraded };
}

// =============================================
// Existing Posts Awareness — semantic match between Node canonical_question and WP post content
// =============================================

export interface SemanticMatchPairInput {
  nodeId: string;
  nodeText: string;
  postId: string;
  postText: string;
  // SRO (opcjonalne — jesli matcher Level 3 ma dane, przekazuje je do LLM dla uniqueness test)
  nodeSro?: {
    canonical_question: string;
    primary_entity: string;
    required_entities: string[];
    query_frame?: string | null;
  };
  postSro?: {
    title: string;
    extracted_entity?: string | null;
    extracted_attribute?: string | null;
    extracted_query_frame?: string | null;
    first_100_words?: string | null;
    first_paragraph_answer?: string | null;
  };
}

export interface SemanticMatchPairResult {
  nodeId: string;
  postId: string;
  decision: 'same' | 'partial_overlap' | 'different';
  confidence: number;
  explanation: string;
}

export interface SemanticMatchBatchStats {
  llm_calls: number;
  llm_tokens_in: number;
  llm_tokens_out: number;
  llm_cost_usd: number;
  used_llm: boolean;
}

export type SemanticMatchLogCallback = (
  type: 'info' | 'success' | 'error' | 'stage',
  message: string,
  debug?: Record<string, unknown>,
) => void;

export interface SemanticMatchOptions {
  onLog?: SemanticMatchLogCallback;
  batchSize?: number;   // default 10
  concurrency?: number; // default 1 (sequential)
}

const WP_MATCHING_SCHEMA: Record<string, unknown> = {
  type: 'object',
  properties: {
    results: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          pair_id: { type: 'string' },
          decision: { type: 'string', enum: ['same', 'partial', 'different'] },
          confidence: { type: 'number' },
          entity_match: { type: 'string', enum: ['same', 'related', 'different'] },
          attribute_match: { type: 'string', enum: ['same', 'related', 'different'] },
          query_frame_compatible: { type: 'boolean' },
          first_paragraph_answers_node_question: { type: 'boolean' },
          reasoning: { type: 'string' },
        },
        required: [
          'pair_id',
          'decision',
          'confidence',
          'entity_match',
          'attribute_match',
          'query_frame_compatible',
          'first_paragraph_answers_node_question',
          'reasoning',
        ],
        additionalProperties: false,
      },
    },
  },
  required: ['results'],
  additionalProperties: false,
};

/**
 * Porownuje pary node↔wp_post i decyduje same/partial/different.
 *
 * Flow:
 *  1) loadPrompt('wp_matching') — jesli istnieje i pair ma nodeSro/postSro → LLM batch call
 *  2) Inaczej fallback Jaccard (legacy behavior, jak byl przed M10)
 *
 * LLM batch: 10 par per request (zgodnie z planem). gpt-4.1-mini, temp 0.
 *
 * Zwraca tez stats (poza results) — nowy shape. Dla backward compat istniejacy kod
 * moze ignorowac stats.
 */
export async function checkSemanticMatchPairs(
  pairs: SemanticMatchPairInput[],
  options: SemanticMatchOptions = {},
): Promise<SemanticMatchPairResult[]> {
  const { results } = await checkSemanticMatchPairsWithStats(pairs, options);
  return results;
}

export async function checkSemanticMatchPairsWithStats(
  pairs: SemanticMatchPairInput[],
  options: SemanticMatchOptions = {},
): Promise<{ results: SemanticMatchPairResult[]; stats: SemanticMatchBatchStats }> {
  const stats: SemanticMatchBatchStats = {
    llm_calls: 0,
    llm_tokens_in: 0,
    llm_tokens_out: 0,
    llm_cost_usd: 0,
    used_llm: false,
  };

  if (pairs.length === 0) return { results: [], stats };

  const onLog = options.onLog;
  const batchSize = Math.max(1, options.batchSize ?? 10);
  const concurrency = Math.max(1, Math.min(10, options.concurrency ?? 1));

  const prompt = await loadPrompt('wp_matching');
  // Pary mają SRO (fuzzy minimum: nodeSro jest = kazde node ma kanonik pytanie)
  const hasSroData = pairs.every((p) => p.nodeSro !== undefined);

  // Jesli prompt + SRO → LLM batch
  if (prompt && hasSroData) {
    stats.used_llm = true;
    const totalBatches = Math.ceil(pairs.length / batchSize);
    logPublic('WPMatch', `LLM wp_matching — ${pairs.length} par w ${totalBatches} batchach (size=${batchSize}, concurrency=${concurrency})`);
    onLog?.('info', `[WPMatch] start — ${pairs.length} par, ${totalBatches} batchy (size=${batchSize}, concurrency=${concurrency})`);

    // Przygotuj liste batchy (index 1..totalBatches dla czytelnego logu)
    type Batch = { idx: number; startI: number; pairs: SemanticMatchPairInput[] };
    const batches: Batch[] = [];
    for (let i = 0; i < pairs.length; i += batchSize) {
      batches.push({ idx: batches.length + 1, startI: i, pairs: pairs.slice(i, i + batchSize) });
    }

    // Per-batch runner (zwraca wyniki + mutuje stats — atomic-ish via local acc)
    const runBatch = async (b: Batch): Promise<SemanticMatchPairResult[]> => {
      const pairsForLlm = b.pairs.map((p, idx) => ({
        pair_id: `p${b.startI + idx + 1}`,
        node: {
          canonical_question: p.nodeSro?.canonical_question ?? p.nodeText,
          primary_entity: p.nodeSro?.primary_entity ?? '',
          required_entities: p.nodeSro?.required_entities ?? [],
          query_frame: p.nodeSro?.query_frame ?? null,
        },
        wp_post: {
          title: p.postSro?.title ?? p.postText.split('.')[0] ?? '',
          extracted_entity: p.postSro?.extracted_entity ?? 'UNKNOWN',
          extracted_attribute: p.postSro?.extracted_attribute ?? 'UNKNOWN',
          extracted_query_frame: p.postSro?.extracted_query_frame ?? 'UNKNOWN',
          first_100_words: p.postSro?.first_100_words ?? '',
          first_paragraph_answer: p.postSro?.first_paragraph_answer ?? '',
        },
      }));

      const compiledPrompt = fillTemplate(prompt.template, {
        pairs_json: JSON.stringify(pairsForLlm, null, 2),
      });

      const batchResults: SemanticMatchPairResult[] = [];
      const t0 = Date.now();

      try {
        const response = await openaiProvider.call(compiledPrompt, {
          model: prompt.modelId || 'gpt-4.1-mini',
          temperature: prompt.temperature ?? 0.0,
          jsonSchema: WP_MATCHING_SCHEMA,
        });
        stats.llm_calls++;
        stats.llm_tokens_in += response.tokensInput;
        stats.llm_tokens_out += response.tokensOutput;
        stats.llm_cost_usd += response.costUsd ?? 0;

        const parsed = JSON.parse(response.text) as {
          results: Array<{
            pair_id: string;
            decision: 'same' | 'partial' | 'different';
            confidence: number;
            entity_match: 'same' | 'related' | 'different';
            attribute_match: 'same' | 'related' | 'different';
            query_frame_compatible: boolean;
            first_paragraph_answers_node_question: boolean;
            reasoning: string;
          }>;
        };

        const sample: Array<Record<string, unknown>> = [];
        for (const r of parsed.results) {
          const globalIdx = parseInt(r.pair_id.slice(1)) - 1;
          const original = pairs[globalIdx];
          if (!original) continue;

          const decisionMapped: 'same' | 'partial_overlap' | 'different' =
            r.decision === 'partial' ? 'partial_overlap' : r.decision;

          batchResults.push({
            nodeId: original.nodeId,
            postId: original.postId,
            decision: decisionMapped,
            confidence: r.confidence,
            explanation: `[LLM] ${r.reasoning} (entity:${r.entity_match}, attribute:${r.attribute_match}, frame:${r.query_frame_compatible ? 'ok' : 'mismatch'}, fpa:${r.first_paragraph_answers_node_question ? 'yes' : 'no'})`,
          });

          if (sample.length < 3) {
            sample.push({
              pair_id: r.pair_id,
              decision: r.decision,
              confidence: r.confidence,
              reasoning: r.reasoning,
              node_question: pairsForLlm.find((x) => x.pair_id === r.pair_id)?.node.canonical_question,
              wp_title: pairsForLlm.find((x) => x.pair_id === r.pair_id)?.wp_post.title,
            });
          }
        }

        const dtMs = Date.now() - t0;
        onLog?.(
          'stage',
          `[WPMatch] batch ${b.idx}/${totalBatches} — ${b.pairs.length} par, ${dtMs}ms, $${(response.costUsd ?? 0).toFixed(4)}`,
          {
            batch_idx: b.idx,
            total_batches: totalBatches,
            pairs_count: b.pairs.length,
            duration_ms: dtMs,
            tokens_in: response.tokensInput,
            tokens_out: response.tokensOutput,
            cost_usd: response.costUsd ?? 0,
            sample_results: sample,
            compiled_prompt: compiledPrompt,
            pairs_input: pairsForLlm,
            response_text: response.text,
          },
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        logDebug('WPMatch', `LLM batch ${b.idx} failed: ${msg} — fallback Jaccard`);
        onLog?.(
          'error',
          `[WPMatch] batch ${b.idx}/${totalBatches} LLM failed — fallback Jaccard: ${msg}`,
          { batch_idx: b.idx, error: msg, compiled_prompt: compiledPrompt },
        );
        for (const p of b.pairs) {
          const jacc = jaccardSimilarity(tokenize(p.nodeText), tokenize(p.postText));
          let decision: 'same' | 'partial_overlap' | 'different';
          if (jacc >= 0.7) decision = 'same';
          else if (jacc >= 0.5) decision = 'partial_overlap';
          else decision = 'different';
          batchResults.push({
            nodeId: p.nodeId,
            postId: p.postId,
            decision,
            confidence: decision === 'different' ? 1 - jacc : jacc,
            explanation: `fallback_jaccard_after_llm_fail(${(jacc * 100).toFixed(0)}%)`,
          });
        }
      }

      return batchResults;
    };

    // Wykonaj batches chunked Promise.all (concurrency window)
    const allResults: SemanticMatchPairResult[] = [];
    for (let i = 0; i < batches.length; i += concurrency) {
      const chunk = batches.slice(i, i + concurrency);
      const chunkResults = await Promise.all(chunk.map(runBatch));
      for (const r of chunkResults) allResults.push(...r);
    }

    return { results: allResults, stats };
  }

  // Fallback Jaccard (brak promptu lub brak SRO w parach)
  logDebug('WPMatch', `Using fallback Jaccard — ${pairs.length} pairs (prompt=${!!prompt}, sroData=${hasSroData})`);
  const results: SemanticMatchPairResult[] = [];
  for (const p of pairs) {
    const jacc = jaccardSimilarity(tokenize(p.nodeText), tokenize(p.postText));
    let decision: 'same' | 'partial_overlap' | 'different';
    let explanation: string;

    if (jacc >= 0.7) {
      decision = 'same';
      explanation = `fallback_high_jaccard(${(jacc * 100).toFixed(0)}%)`;
    } else if (jacc >= 0.5) {
      decision = 'partial_overlap';
      explanation = `fallback_medium_jaccard(${(jacc * 100).toFixed(0)}%)`;
    } else {
      decision = 'different';
      explanation = `fallback_low_jaccard(${(jacc * 100).toFixed(0)}%)`;
    }

    results.push({
      nodeId: p.nodeId,
      postId: p.postId,
      decision,
      confidence: decision === 'different' ? 1 - jacc : jacc,
      explanation,
    });
  }

  return { results, stats };
}
